import { useState, useMemo, createContext, useContext } from 'react'
import {
  LightspeedBusiness,
  LightspeedStore
} from 'components/Integrations/Manage/ManageLightspeed/types'
import { ShipdayIntegration } from 'components/Integrations/Manage/ManageShipday/types'
import { CustomOrderWebhook } from 'components/Integrations/Manage/ManageCustomWebhook/types'
import { MerchantIntegrationSettings } from 'components/Integrations/types'

type IntegrationState = {
  merchantIntegrations: MerchantIntegrationSettings | null
  setMerchantIntegrations: (data: MerchantIntegrationSettings | null) => void
  refetchMerchantIntegrations: () => Promise<unknown>
  setRefetchMerchantIntegrations: (refetch: () => Promise<unknown>) => void
  lightspeedBusinesses: LightspeedBusiness[]
  setLightspeedBusinesses: (businesses: LightspeedBusiness[]) => void
  lightspeedAuthExpired: boolean
  setLightspeedAuthExpired: (expired: boolean) => void
  lightspeedStores: LightspeedStore[]
  setLightspeedStores: (stores: LightspeedStore[]) => void
  lightspeedStoresLoading: boolean
  setLightspeedStoresLoading: (loading: boolean) => void
  refetchLightspeedStores: () => Promise<unknown>
  setRefetchLightspeedStores: (refetch: () => Promise<unknown>) => void
  shipdayIntegrations: ShipdayIntegration[]
  setShipdayIntegrations: (integrations: ShipdayIntegration[]) => void
  refetchShipdayIntegrations: () => Promise<unknown>
  setRefetchShipdayIntegrations: (refetch: () => Promise<unknown>) => void
  customWebhooks: CustomOrderWebhook[]
  setCustomWebhooks: (webhooks: CustomOrderWebhook[]) => void
  // Fetched-at-all, independent of length — a merchant with zero custom
  // webhooks must still count as loaded.
  customWebhooksLoaded: boolean
  setCustomWebhooksLoaded: (loaded: boolean) => void
}

export const SetupIntegration = (): IntegrationState => {
  const [merchantIntegrations, setMerchantIntegrations] =
    useState<MerchantIntegrationSettings | null>(null)
  const [refetchMerchantIntegrations, setRefetchMerchantIntegrations] =
    useState<() => Promise<unknown>>(() => () => Promise.resolve())

  const [lightspeedBusinesses, setLightspeedBusinesses] = useState<
    LightspeedBusiness[]
  >([])
  const [lightspeedAuthExpired, setLightspeedAuthExpired] = useState(false)

  const [lightspeedStores, setLightspeedStores] = useState<LightspeedStore[]>(
    []
  )
  const [lightspeedStoresLoading, setLightspeedStoresLoading] = useState(false)
  const [refetchLightspeedStores, setRefetchLightspeedStores] = useState<
    () => Promise<unknown>
  >(() => () => Promise.resolve())

  const [shipdayIntegrations, setShipdayIntegrations] = useState<
    ShipdayIntegration[]
  >([])
  const [refetchShipdayIntegrations, setRefetchShipdayIntegrations] = useState<
    () => Promise<unknown>
  >(() => () => Promise.resolve())

  const [customWebhooks, setCustomWebhooks] = useState<CustomOrderWebhook[]>([])
  const [customWebhooksLoaded, setCustomWebhooksLoaded] = useState(false)

  return useMemo(
    () => ({
      merchantIntegrations,
      setMerchantIntegrations,
      refetchMerchantIntegrations,
      setRefetchMerchantIntegrations,
      lightspeedBusinesses,
      setLightspeedBusinesses,
      lightspeedAuthExpired,
      setLightspeedAuthExpired,
      lightspeedStores,
      setLightspeedStores,
      lightspeedStoresLoading,
      setLightspeedStoresLoading,
      refetchLightspeedStores,
      setRefetchLightspeedStores,
      shipdayIntegrations,
      setShipdayIntegrations,
      refetchShipdayIntegrations,
      setRefetchShipdayIntegrations,
      customWebhooks,
      setCustomWebhooks,
      customWebhooksLoaded,
      setCustomWebhooksLoaded
    }),
    [
      merchantIntegrations,
      refetchMerchantIntegrations,
      lightspeedBusinesses,
      lightspeedAuthExpired,
      lightspeedStores,
      lightspeedStoresLoading,
      refetchLightspeedStores,
      shipdayIntegrations,
      refetchShipdayIntegrations,
      customWebhooks,
      customWebhooksLoaded
    ]
  )
}

const initialState: IntegrationState = {
  merchantIntegrations: null,
  setMerchantIntegrations: () => null,
  refetchMerchantIntegrations: () => Promise.resolve(),
  setRefetchMerchantIntegrations: () => null,
  lightspeedBusinesses: [],
  setLightspeedBusinesses: () => null,
  lightspeedAuthExpired: false,
  setLightspeedAuthExpired: () => null,
  lightspeedStores: [],
  setLightspeedStores: () => null,
  lightspeedStoresLoading: false,
  setLightspeedStoresLoading: () => null,
  refetchLightspeedStores: () => Promise.resolve(),
  setRefetchLightspeedStores: () => null,
  shipdayIntegrations: [],
  setShipdayIntegrations: () => null,
  refetchShipdayIntegrations: () => Promise.resolve(),
  setRefetchShipdayIntegrations: () => null,
  customWebhooks: [],
  setCustomWebhooks: () => null,
  customWebhooksLoaded: false,
  setCustomWebhooksLoaded: () => null
}

export const IntegrationContext = createContext<IntegrationState>(initialState)

export const useIntegration = () => {
  return useContext(IntegrationContext)
}
